import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useModal } from "../context/Modal";
import { signUp } from "../store/session";
import { FiUser, FiMail, FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import LoginForm from "./LoginForm";

function SignupForm() {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { closeModal, setModalContent } = useModal();

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const validatePassword = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password);
  const validateUsername = (username) => /^[a-zA-Z0-9_.-]{3,20}$/.test(username);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  // Same scoped modal-shell override as LoginForm — restored on unmount so
  // every other modal keeps its default styling.
  useEffect(() => {
    const el = document.getElementById("modal-content");
    if (!el) return undefined;
    const prev = {
      background: el.style.background,
      boxShadow: el.style.boxShadow,
      borderRadius: el.style.borderRadius,
      maxWidth: el.style.maxWidth,
      overflow: el.style.overflow,
    };
    el.style.background = "transparent";
    el.style.boxShadow = "none";
    el.style.borderRadius = "0";
    el.style.maxWidth = "460px";
    el.style.overflow = "visible";
    const bg = document.getElementById("modal-background");
    let prevBg = null;
    if (bg) {
      prevBg = bg.style.backgroundColor;
      bg.style.backgroundColor = "rgba(15, 23, 42, 0.55)";
    }
    return () => {
      Object.assign(el.style, prev);
      if (bg) bg.style.backgroundColor = prevBg;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    let validationErrors = {};

    if (!validateEmail(email)) validationErrors.email = "Invalid email address.";
    if (!validateUsername(username))
      validationErrors.username =
        "Username must be 3-20 characters long and contain only letters, numbers, dots, dashes, or underscores.";
    if (!validatePassword(password))
      validationErrors.password =
        "Password must be at least 8 characters long and include an uppercase letter, a number, and a special character.";
    if (password !== confirmPassword)
      validationErrors.confirmPassword = "Passwords do not match.";

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    const data = await dispatch(signUp(username.trim(), email.trim(), password, confirmPassword));
    if (data) {
      setErrors({ form: data });
      setIsSubmitting(false);
    } else {
      closeModal();
    }
  };

  const openLogin = () => setModalContent(<LoginForm />);

  const glassPanel = {
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.10) 60%, rgba(255,255,255,0.20) 100%)",
    backdropFilter: "blur(24px) saturate(180%)",
    WebkitBackdropFilter: "blur(24px) saturate(180%)",
    border: "1px solid rgba(255,255,255,0.45)",
    boxShadow:
      "0 8px 32px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.55)",
    borderRadius: "1.75rem",
  };
  const glassField = (hasError) =>
    `w-full h-12 pl-12 pr-12 rounded-full bg-white/10 border ${
      hasError ? "border-red-300" : "border-white/40"
    } text-white placeholder:text-white/70 focus:outline-none focus:border-white/80 focus:bg-white/15 transition-colors`;
  const fieldErrorPill = {
    background: "rgba(220, 38, 38, 0.25)",
    border: "1px solid rgba(254, 202, 202, 0.65)",
    color: "#fff",
    backdropFilter: "blur(6px)",
  };

  return (
    <div className="p-8 sm:p-10 text-white" style={glassPanel}>
      <h2 className="text-4xl font-bold mb-2 tracking-tight drop-shadow">Signup</h2>
      <p className="text-white/85 mb-8 text-sm">
        Create your account to sync memos, highlights, and reading progress.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {errors.form && (
          <p className="rounded-2xl px-4 py-2 text-sm" style={fieldErrorPill}>
            {errors.form}
          </p>
        )}

        {/* Email */}
        <div>
          <div className="relative">
            <FiMail
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 pointer-events-none"
              size={18}
              aria-hidden="true"
            />
            <input
              id="email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Email"
              aria-label="Email"
              aria-describedby={errors.email ? "email-error" : null}
              className={glassField(Boolean(errors.email))}
            />
          </div>
          {errors.email && (
            <p id="email-error" className="text-white text-xs mt-1 pl-2" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
              {errors.email}
            </p>
          )}
        </div>

        {/* Username */}
        <div>
          <div className="relative">
            <FiUser
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 pointer-events-none"
              size={18}
              aria-hidden="true"
            />
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="User Name"
              aria-label="Username"
              aria-describedby={errors.username ? "username-error" : null}
              className={glassField(Boolean(errors.username))}
            />
          </div>
          {errors.username && (
            <p id="username-error" className="text-white text-xs mt-1 pl-2" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
              {errors.username}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="relative">
            <FiLock
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 pointer-events-none"
              size={18}
              aria-hidden="true"
            />
            <input
              id="password"
              type={isPasswordVisible ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Password"
              aria-label="Password"
              aria-describedby={errors.password ? "password-error" : null}
              className={glassField(Boolean(errors.password))}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors"
              aria-label={isPasswordVisible ? "Hide password" : "Show password"}
            >
              {isPasswordVisible ? <FiEye size={18} /> : <FiEyeOff size={18} />}
            </button>
          </div>
          {errors.password && (
            <p id="password-error" className="text-white text-xs mt-1 pl-2" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
              {errors.password}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <div className="relative">
            <FiLock
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 pointer-events-none"
              size={18}
              aria-hidden="true"
            />
            <input
              id="confirm-password"
              type={isPasswordVisible ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm Password"
              aria-label="Confirm password"
              aria-describedby={errors.confirmPassword ? "confirm-password-error" : null}
              className={glassField(Boolean(errors.confirmPassword))}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors"
              aria-label={isPasswordVisible ? "Hide password" : "Show password"}
            >
              {isPasswordVisible ? <FiEye size={18} /> : <FiEyeOff size={18} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p id="confirm-password-error" className="text-white text-xs mt-1 pl-2" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
              {errors.confirmPassword}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-14 rounded-full text-white text-lg font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.08) 100%)",
            border: "1px solid rgba(255,255,255,0.6)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.6), 0 6px 20px rgba(0,0,0,0.25)",
            backdropFilter: "blur(6px)",
          }}
        >
          {isSubmitting ? "Signing Up…" : "Signup"}
        </button>

        <p className="text-center text-white/90 text-sm pt-1">
          Already have an account?{" "}
          <button
            type="button"
            onClick={openLogin}
            className="font-semibold underline underline-offset-2 hover:text-white"
          >
            Login
          </button>
        </p>
      </form>
    </div>
  );
}

export default SignupForm;
